<template lang="pug">
.vue-app-datetime-range-picker.col-sm-12(:class="{ 'vue-app-datetime-range-picker-popover': popover }")
  .datepicker-container(
    v-if="showDatepicker"
    :class="{ 'col-sm-5': !popover, 'col-sm-12': popover }"
  )
    AppDatePicker.pull-left.pr-0(
      v-model="startDate"
      type="daily"
      :value="startDate"
      :min-date="minDate"
      :max-date="maxDate"
      :start-week-on="startWeekOn"
      :show-quick-pick="false"
      :disabled="disableDate"
      :extra-picker-classes="extraPickerClasses"
      :current-zone="currentZone"
    )
  ValidationProvider.from-hour-select.block-form-group.no-padding-left-sm(
    v-slot="{ errors }"
    tag="div"
    rules="future"
    :class="{ 'col-sm-3': primary, 'col-sm-4': timespan, 'col-sm-6': popover }"
  )
    AppTimeInput(
      v-model="startTime"
      :type="type"
      :ceiling="ceiling"
      :floor="floor"
      :class="{ 'has-error': errors[0] }"
      name="start_time"
    )
      span.help-block {{ errors[0] }}
  ValidationProvider.to-hour-select.block-form-group.no-padding-right-sm(
    v-slot="{ errors }"
    tag="div"
    rules="future"
    :class="{ 'col-sm-3': primary, 'col-sm-4': timespan, 'col-sm-6': popover }"
  )
    span.badge.badge-pill.badge-secondary.badge-next-day(v-if="isNextDay") Next day
    AppTimeInput(
      v-model="stopTime"
      :type="type"
      :ceiling="ceiling"
      :floor="floor"
      :show-yesterday="isNextDay"
      :class="{ 'has-error': errors[0] }"
      name="stop_time"
    )
      span.help-block {{ errors[0] }}
  slot(
    v-if="timespan"
    name="timeZone"
  )
  .col-sm-12
    slot(name="extraErrors")
</template>

<script>
import { extend } from 'vee-validate';
import { createWithZone } from '~/utils/time_utils';

extend('future', {
  validate: (value) => {
    return !value.isAfter(moment());
  },
  message: 'Cannot be in the future',
});

export default {
  name: 'AppDatetimeRangePicker',
  props: {
    value: {
      type: Object,
      required: true,
    },
    minDate: {
      type: String,
      required: true,
    },
    maxDate: {
      type: String,
      required: true,
    },
    startWeekOn: {
      type: Number,
      default: 0,
    },
    extraPickerClasses: {
      type: String,
    },
    showDatepicker: {
      type: Boolean,
      default: true,
    },
    timezone: {
      type: String,
      default: null,
    },
    moveStopInParallel: {
      type: Boolean,
      default: false,
    },
    ceiling: {
      type: Object,
      default: () => moment(),
    },
    floor: {
      type: Object,
      default: () => {},
    },
    disableDate: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'primary',
    },
    currentZone: {
      type: Object,
      required: true,
    },
  },
  computed: {
    startTime: {
      get() {
        return this.value.start;
      },
      set(newValue) {
        const newStartDatetime = this.getDatetime(this.startDate, newValue);
        let newStopDatetime;

        if (this.moveStopInParallel) {
          newStopDatetime = newStartDatetime.clone().add(this.duration);

          if (newStopDatetime.isAfter(this.now())) {
            newStopDatetime = this.now().clone().seconds(0).milliseconds(0);
          }
        } else {
          newStopDatetime = this.value.stop;
        }

        this.$emit('input', {
          start: newStartDatetime,
          stop: newStopDatetime,
        });
      },
    },
    startDate: {
      get() {
        return this.value.start;
      },
      set(newValue) {
        const newStartDatetime = this.getDatetime(newValue, this.startTime);
        const newStopDatetime = newStartDatetime.clone().add(this.duration);

        this.$emit('input', {
          start: newStartDatetime,
          stop: newStopDatetime,
        });
      },
    },
    stopTime: {
      get() {
        return this.value.stop;
      },
      set(newValue) {
        const stopDatetimeOnSelectedDay = this.getDatetime(this.startDate, newValue);
        const newStopDatetime =
          stopDatetimeOnSelectedDay <= this.value.start
            ? stopDatetimeOnSelectedDay.clone().add(this.oneDay)
            : stopDatetimeOnSelectedDay;

        this.$emit('input', {
          start: this.value.start,
          stop: newStopDatetime,
        });
      },
    },
    duration() {
      return moment.duration(this.value.stop - this.value.start);
    },
    isNextDay() {
      return !this.value.start.isSame(this.value.stop, 'day');
    },
    isToday() {
      return this.value.start.isSame(this.now(), 'day');
    },
    oneDay() {
      return moment.duration(1, 'day');
    },
    primary() {
      return this.type === 'primary';
    },
    popover() {
      return this.type === 'popover';
    },
    timespan() {
      return this.type === 'timespan';
    },
  },
  methods: {
    now() {
      return moment().tz(this.timezone);
    },
    getDatetime(date, time) {
      return this.timezone
        ? createWithZone(date, time, this.timezone)
        : date
            .clone()
            .hours(time.hours())
            .minutes(time.minutes())
            .seconds(time.seconds())
            .milliseconds(time.milliseconds());
    },
  },
};
</script>
